import { Box } from '@mui/material';
import HereNativeRouteMap, {
    HereMarker,
} from 'components/Common/HereMaps/HereNativeRouteMap';
import { drawerWidth } from 'materialUI/jss/material-dashboard-pro-react';
import React from 'react';

export interface Location {
  formattedAddress?: string
  addressLine1?: string
  town?: string
  postCode?: string
  country?: string
  countryCode?: string
  latitude: number
  longitude: number
  [key: string]: any
}

interface MapBackgroundProps {
  pickup?: {
    latitude: number
    longitude: number
    formattedAddress?: string
    [key: string]: any
  }
  dropoff?: {
    latitude: number
    longitude: number
    formattedAddress?: string
    [key: string]: any
  }
  stops?: Array<{ latitude: number; longitude: number; [key: string]: any }>
  onRouteInfoChange?: (info: {
    estimatedJourneyDistance: number
    estimatedJourneyTime: number
  }) => void

  flightArc?: {
    from: { latitude: number; longitude: number }
    to: { latitude: number; longitude: number }
  }
  flightArcFlyoverKey?: number
  markers?: HereMarker[]

  allowFitBounds?: boolean
  onBoundsChange?: (bounds: {
    north: number
    south: number
    east: number
    west: number
  }) => void

  panTarget?: { latitude: number; longitude: number }
  panZoom?: number
  panAnimated?: boolean

  showRouteMarkers?: boolean

  extraRoutes?: Array<{
    key: string
    origin: { latitude: number; longitude: number }
    destination: { latitude: number; longitude: number }
    color?: string
  }>
  fitToPoints?: Array<{ latitude: number; longitude: number }>
  viewportPadding?: {
    top?: number
    right?: number
    bottom?: number
    left?: number
  }
  htmlOverlays?: Array<{
    key: string
    position: { latitude: number; longitude: number }
    size: { width: number; height: number }
    tailHeight?: number
    preferAwayFrom?: { latitude: number; longitude: number }
    render: (layout: {
      tailSide: 'top' | 'right' | 'bottom' | 'left'
      tailAttach: number
    }) => React.ReactNode
  }>
}

const MapBackground: React.FC<MapBackgroundProps> = ({
  pickup,
  dropoff,
  stops = [],
  onRouteInfoChange,
  flightArc,
  flightArcFlyoverKey,
  markers,
  allowFitBounds = true,
  onBoundsChange,
  panTarget,
  panZoom,
  panAnimated,
  showRouteMarkers = true,
  extraRoutes,
  htmlOverlays,
  fitToPoints,
  viewportPadding,
}) => {
  const origin = pickup
    ? {
        latitude: pickup.latitude,
        longitude: pickup.longitude,
      }
    : undefined

  const destination = dropoff
    ? {
        latitude: dropoff.latitude,
        longitude: dropoff.longitude,
      }
    : undefined

  const additionalStops = stops.map((stop) => ({
    latitude: stop.latitude,
    longitude: stop.longitude,
  }))

  const handleRouteInfoUpdate = (info: {
    estimatedJourneyTime: number
    estimatedJourneyDistance: number
    directionsResponse: any
  }) => {
    if (onRouteInfoChange) {
      onRouteInfoChange({
        estimatedJourneyDistance: info.estimatedJourneyDistance,
        estimatedJourneyTime: info.estimatedJourneyTime,
      })
    }
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 70,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        zIndex: 0,
        backgroundColor: '#f0f0f0',

        '& .leaflet-control-attribution': {
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          fontSize: '11px',
          padding: '4px 6px',
        },

        '& .leaflet-control-zoom': {
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        },

        '& .leaflet-top.leaflet-left': {
          left: 'auto',
          right: 0,
        },
      }}
    >
      <HereNativeRouteMap
        origin={origin}
        destination={destination}
        additionalStops={additionalStops}
        mapWidth="100%"
        mapHeight="100%"
        markers={markers}
        showRoute={!!(origin && destination)}
        showRouteMarkers={showRouteMarkers}
        allowFitBounds={allowFitBounds}
        showMapTypeControl={true}
        updateRouteInfo={onRouteInfoChange ? handleRouteInfoUpdate : undefined}
        onBoundsChange={onBoundsChange}
        viewportPadding={{
          top: viewportPadding?.top ?? 40,
          left: viewportPadding?.left ?? 592,
          bottom: viewportPadding?.bottom ?? 40,
          right: viewportPadding?.right ?? 40,
        }}
        flightArc={
          flightArc?.from?.latitude != null &&
          flightArc?.from?.longitude != null &&
          flightArc?.to?.latitude != null &&
          flightArc?.to?.longitude != null
            ? {
                from: {
                  latitude: flightArc.from.latitude,
                  longitude: flightArc.from.longitude,
                },
                to: {
                  latitude: flightArc.to.latitude,
                  longitude: flightArc.to.longitude,
                },
              }
            : undefined
        }
        flightArcFlyoverKey={flightArcFlyoverKey}
        panTarget={
          panTarget && panTarget.latitude != null && panTarget.longitude != null
            ? { latitude: panTarget.latitude, longitude: panTarget.longitude }
            : undefined
        }
        panZoom={panZoom}
        panAnimated={panAnimated}
        extraRoutes={extraRoutes}
        htmlOverlays={htmlOverlays}
        fitToPoints={fitToPoints?.map((p) => ({
          latitude: p.latitude,
          longitude: p.longitude,
        }))}
        mapOptions={{
          zoomControl: true,
          scrollWheelZoom: true,
          dragging: true,
          doubleClickZoom: true,
          touchZoom: true,
        }}
      />
    </Box>
  )
}

export default MapBackground
